import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Space, Table, Tag, Tooltip, Typography } from 'antd';
import {
  ApartmentOutlined,
  DisconnectOutlined,
  KeyOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { ColumnsType } from 'antd/lib/table';
import BreadCrumbs from '../../../common/components/Breadcrumbs';
import { Action, Page } from '../../../common/constants/pageAction';
import ability from '../../../common/ability';
import azureOnboardingService from '../../../services/azure/azureOnboarding/azureOnboarding.service';
import {
  AzureOnboardingListItem,
  AzureScope,
} from '../../../services/azure/azureOnboarding/azureOnboarding.model';
import {
  clientLink,
  copyText,
  getStatusDisplay,
  responseErrorText,
  secretExpiryDisplay,
} from './azureOnboarding.utils';
import NewOnboardingModal from './components/NewOnboardingModal';
import ConnectClientAppModal from './components/ConnectClientAppModal';
import VerifyResultModal, { VerifyState } from './components/VerifyResultModal';
import RediscoverResultModal, { RediscoverState } from './components/RediscoverResultModal';
import ScopesModal from './components/ScopesModal';
import ResetTenantModal from './components/ResetTenantModal';

const { Text } = Typography;

const rowTitle = (row: AzureOnboardingListItem) =>
  `${row.company_name ?? '–'} / ${row.bu_name ?? '–'}`;

const byText = (a: string | null, b: string | null) => (a ?? '').localeCompare(b ?? '');

const AzureOnboarding: React.FC = () => {
  const [data, setData] = useState<AzureOnboardingListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [newVisible, setNewVisible] = useState(false);
  const [connectVisible, setConnectVisible] = useState(false);
  const [copyingId, setCopyingId] = useState<number | null>(null);

  const [scopesRow, setScopesRow] = useState<AzureOnboardingListItem | null>(null);
  const [scopes, setScopes] = useState<AzureScope[]>([]);
  const [scopesLoading, setScopesLoading] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  // Async handlers need the open panel's row without waiting for a re-render.
  const scopesRowId = useRef<number | null>(null);

  const [verifyState, setVerifyState] = useState<VerifyState | null>(null);
  const [rediscoverState, setRediscoverState] = useState<RediscoverState | null>(null);
  const [resetRow, setResetRow] = useState<AzureOnboardingListItem | null>(null);

  const canAdd = ability.can(Action.Add, Page.AzureOnboarding);
  const canUpdate = ability.can(Action.Update, Page.AzureOnboarding);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await azureOnboardingService.list();
      setData(Array.isArray(result) ? result : []);
    } catch {
      /* handled by request interceptor */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadScopes = async (oauthId: number) => {
    setScopesLoading(true);
    try {
      const result = await azureOnboardingService.getScopes(oauthId);
      if (scopesRowId.current === oauthId) setScopes(result);
    } catch {
      /* handled by request interceptor */
    } finally {
      setScopesLoading(false);
    }
  };

  const openScopes = (row: AzureOnboardingListItem) => {
    scopesRowId.current = row.oauth_id;
    setScopesRow(row);
    setScopes([]);
    loadScopes(row.oauth_id);
  };

  const closeScopes = () => {
    scopesRowId.current = null;
    setScopesRow(null);
    setScopes([]);
  };

  const handleCopyLink = async (row: AzureOnboardingListItem) => {
    setCopyingId(row.oauth_id);
    try {
      const result = await azureOnboardingService.getConsentUrl(row.oauth_id);
      await copyText(clientLink(result.link_token));
      toast.success('Client link copied');
    } catch {
      /* handled by request interceptor */
    } finally {
      setCopyingId(null);
    }
  };

  const handleVerify = async (row: AzureOnboardingListItem) => {
    setVerifyState({
      title: `Verify: ${rowTitle(row)}`,
      loading: true,
      scopes: [],
      clientManaged: row.client_managed,
    });
    try {
      const result = await azureOnboardingService.verify(row.oauth_id);
      let rowScopes: AzureScope[] = [];
      try {
        rowScopes = await azureOnboardingService.getScopes(row.oauth_id);
      } catch {
        /* display names are optional here */
      }
      setVerifyState((s) => s && { ...s, loading: false, result, scopes: rowScopes });
      if (scopesRowId.current === row.oauth_id) setScopes(rowScopes);
    } catch (err) {
      setVerifyState((s) => s && { ...s, loading: false, error: responseErrorText(err) });
    }
    load();
  };

  const handleRediscover = async (row: AzureOnboardingListItem) => {
    setRediscoverState({ title: `Rediscover: ${rowTitle(row)}`, loading: true });
    try {
      const result = await azureOnboardingService.rediscover(row.oauth_id);
      setRediscoverState((s) => s && { ...s, loading: false, result });
      if (scopesRowId.current === row.oauth_id) setScopes(result.scopes);
      load();
    } catch (err) {
      setRediscoverState((s) => s && { ...s, loading: false, error: responseErrorText(err) });
    }
  };

  const handleToggleScope = async (scope: AzureScope, active: boolean) => {
    setTogglingId(scope.id);
    try {
      await azureOnboardingService.setScopeActive(scope.oauth_id, scope.id, active);
      toast.success(active ? 'Scope switched on' : 'Scope switched off');
      loadScopes(scope.oauth_id);
      load();
    } catch {
      /* handled by request interceptor */
    } finally {
      setTogglingId(null);
    }
  };

  const handleReset = async () => {
    if (!resetRow) return false;
    try {
      await azureOnboardingService.resetTenant(resetRow.oauth_id);
      toast.success(`Azure tenant reset for ${rowTitle(resetRow)}`);
      if (scopesRowId.current === resetRow.oauth_id) loadScopes(resetRow.oauth_id);
      setResetRow(null);
      load();
      return true;
    } catch {
      /* handled by request interceptor */
      return false;
    }
  };

  const columns: ColumnsType<AzureOnboardingListItem> = [
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => byText(a.company_name, b.company_name),
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_name',
      key: 'tenant_name',
      sorter: (a, b) => byText(a.tenant_name, b.tenant_name),
    },
    {
      title: 'BU',
      dataIndex: 'bu_name',
      key: 'bu_name',
      sorter: (a, b) => byText(a.bu_name, b.bu_name),
    },
    {
      title: 'Cloud',
      dataIndex: 'cloud_name',
      key: 'cloud_name',
      sorter: (a, b) => byText(a.cloud_name, b.cloud_name),
      render: (_, record) => record.cloud_name ?? '–',
    },
    {
      title: 'Secret expires',
      key: 'client_secret_expires',
      render: (_, record) => {
        if (!record.client_managed) return <Text type="secondary">–</Text>;
        const display = secretExpiryDisplay(record.client_secret_expires);
        if (!display) return <Text type="secondary">Not recorded</Text>;
        return display.color ? <Tag color={display.color}>{display.label}</Tag> : display.label;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => byText(a.status, b.status),
      render: (_, record) => {
        const display = getStatusDisplay(record.status, record.client_managed);
        return (
          <Space size={4}>
            <Tooltip title={display.tooltip}>
              <Tag color={display.color}>{display.label}</Tag>
            </Tooltip>
            {record.status === 'Connected' && record.scopes.active === 0 && (
              <Tooltip title="All scopes are switched off: nothing is being collected.">
                <WarningOutlined
                  style={{ color: '#faad14' }}
                  aria-label="All scopes are switched off"
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Scopes',
      key: 'scopes',
      render: (_, record) => (
        <>
          <div>
            {record.scopes.active} active / {record.scopes.total} total
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.scopes.connected} connected · {record.scopes.pending} pending ·{' '}
            {record.scopes.failed} failed
          </Text>
        </>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 230,
      render: (_, record) => {
        const awaitingConsent = record.status === 'AwaitingConsent';
        const reconnectTooltip = "Reconnect with Connect with client's own app";
        return (
          <Space>
            {canUpdate && !record.client_managed && (
              <Tooltip title="Copy client link">
                <Button
                  size="small"
                  icon={<LinkOutlined />}
                  loading={copyingId === record.oauth_id}
                  onClick={() => handleCopyLink(record)}
                  aria-label="Copy client link"
                />
              </Tooltip>
            )}
            {canUpdate && (
              <Tooltip title="Verify">
                <Button
                  size="small"
                  icon={<SafetyCertificateOutlined />}
                  onClick={() => handleVerify(record)}
                  aria-label="Verify"
                />
              </Tooltip>
            )}
            {canUpdate && (
              <Tooltip
                title={
                  awaitingConsent
                    ? record.client_managed
                      ? reconnectTooltip
                      : 'Rediscover (available after consent)'
                    : 'Rediscover'
                }
              >
                <Button
                  size="small"
                  icon={<SyncOutlined />}
                  disabled={awaitingConsent}
                  onClick={() => handleRediscover(record)}
                  aria-label="Rediscover"
                />
              </Tooltip>
            )}
            <Tooltip title="Scopes">
              <Button
                size="small"
                icon={<ApartmentOutlined />}
                onClick={() => openScopes(record)}
                aria-label="Scopes"
              />
            </Tooltip>
            {canUpdate && (
              <Tooltip
                title={
                  awaitingConsent
                    ? record.client_managed
                      ? reconnectTooltip
                      : 'Reset tenant (nothing to reset yet)'
                    : 'Reset tenant'
                }
              >
                <Button
                  size="small"
                  danger
                  icon={<DisconnectOutlined />}
                  disabled={awaitingConsent}
                  onClick={() => setResetRow(record)}
                  aria-label="Reset tenant"
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="sqlServer">
      <div className="title-block">
        <h4 className="p-0">
          <BreadCrumbs pageName={Page.AzureOnboarding} />
        </h4>
      </div>

      <div className="main-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
          {canUpdate && (
            <Button icon={<KeyOutlined />} onClick={() => setConnectVisible(true)}>
              Connect with client&apos;s own app
            </Button>
          )}
          {canAdd && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewVisible(true)}>
              New onboarding
            </Button>
          )}
        </div>

        <Table<AzureOnboardingListItem>
          rowKey="oauth_id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No Azure onboardings yet.' }}
        />
      </div>

      {newVisible && (
        <NewOnboardingModal
          existingIds={data.map((d) => d.oauth_id)}
          onClose={() => setNewVisible(false)}
          onCreated={load}
        />
      )}
      {connectVisible && (
        <ConnectClientAppModal
          existingBuIds={data.map((d) => d.bu_id).filter((id): id is number => id != null)}
          onClose={() => setConnectVisible(false)}
          onSaved={(result, title) => {
            setConnectVisible(false);
            load();
            setRediscoverState(
              result.rediscover_error
                ? { title, loading: false, error: result.rediscover_error }
                : { title, loading: false, result: result.rediscover }
            );
          }}
        />
      )}
      {verifyState && (
        <VerifyResultModal state={verifyState} onClose={() => setVerifyState(null)} />
      )}
      {rediscoverState && (
        <RediscoverResultModal state={rediscoverState} onClose={() => setRediscoverState(null)} />
      )}
      {scopesRow && (
        <ScopesModal
          title={`Scopes: ${rowTitle(scopesRow)}`}
          scopes={scopes}
          loading={scopesLoading}
          canUpdate={canUpdate}
          togglingId={togglingId}
          onToggle={handleToggleScope}
          onClose={closeScopes}
        />
      )}
      {resetRow && (
        <ResetTenantModal row={resetRow} onConfirm={handleReset} onClose={() => setResetRow(null)} />
      )}
    </div>
  );
};

export default AzureOnboarding;
